import React, { useState, useRef, useEffect } from 'react';
import { useAppContext } from '../context/AppContext';
import { useAuth } from '../context/AuthContext';

function formatTimestamp(ts) {
  if (!ts) return '';
  try {
    const d = new Date(ts);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleString();
  } catch {
    return '';
  }
}

export function CandidateNotes({ fingerprint }) {
  const {
    addCandidateNote,
    editCandidateNote,
    deleteCandidateNote,
    getCandidateNotes,
  } = useAppContext() || {};
  const { user } = useAuth() || {};

  const messages = getCandidateNotes?.(fingerprint) || [];
  const [draft, setDraft] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState('');
  const scrollRef = useRef(null);
  const currentUid = user?.uid;

  // Auto-scroll to bottom when new messages appear
  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages.length]);

  const handleSend = () => {
    const text = draft.trim();
    if (!text) return;
    addCandidateNote?.(fingerprint, text);
    setDraft('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleEditSave = (msgId) => {
    const text = editText.trim();
    if (!text) return;
    editCandidateNote?.(fingerprint, msgId, text);
    setEditingId(null);
    setEditText('');
  };

  const handleEditKeyDown = (e, msgId) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleEditSave(msgId);
    }
    if (e.key === 'Escape') {
      setEditingId(null);
      setEditText('');
    }
  };

  const handleDelete = (msgId) => {
    deleteCandidateNote?.(fingerprint, msgId);
  };

  return (
    <div style={{ padding: '12px 14px', borderTop: '1px solid #1e293b', background: '#070e1a' }}>
      {messages.length > 0 && (
        <div
          ref={scrollRef}
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
            maxHeight: 300,
            overflowY: 'auto',
            marginBottom: 10,
            paddingRight: 4,
          }}
        >
          {messages.map((msg) => {
            const isSelf = msg.uid === currentUid;
            const isEditing = editingId === msg.id;
            return (
              <div
                key={msg.id}
                style={{
                  alignSelf: isSelf ? 'flex-end' : 'flex-start',
                  maxWidth: '85%',
                  background: isSelf ? '#0f2a44' : '#0a1120',
                  border: `1px solid ${isSelf ? '#1e3a5f' : '#1e293b'}`,
                  borderRadius: 8,
                  padding: '6px 10px',
                  color: '#e2e8f0',
                  fontSize: 13,
                  lineHeight: 1.4,
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: 12,
                    fontSize: 11,
                    color: '#94a3b8',
                    marginBottom: 4,
                  }}
                >
                  <span style={{ fontWeight: 600 }}>{msg.userEmail || msg.uid}</span>
                  <span>
                    {formatTimestamp(msg.createdAt)}
                    {msg.editedAt && <span style={{ marginLeft: 4, fontStyle: 'italic' }}>(edited)</span>}
                  </span>
                </div>
                {isEditing ? (
                  <div>
                    <textarea
                      value={editText}
                      onChange={(e) => setEditText(e.target.value)}
                      onKeyDown={(e) => handleEditKeyDown(e, msg.id)}
                      rows={2}
                      style={{
                        width: '100%', background: '#060d18', border: '1px solid #1e3a5f',
                        borderRadius: 4, color: '#e2e8f0', fontSize: 13, padding: '4px 8px',
                        resize: 'vertical', outline: 'none', fontFamily: 'inherit', lineHeight: 1.5,
                        boxSizing: 'border-box',
                      }}
                      autoFocus
                    />
                    <div style={{ display: 'flex', gap: 6, marginTop: 4, justifyContent: 'flex-end' }}>
                      <button
                        onClick={() => { setEditingId(null); setEditText(''); }}
                        style={{
                          background: 'transparent', border: '1px solid #334155', borderRadius: 4,
                          color: '#94a3b8', fontSize: 11, padding: '2px 8px', cursor: 'pointer',
                        }}
                      >Cancel</button>
                      <button
                        onClick={() => handleEditSave(msg.id)}
                        style={{
                          background: '#1e3a5f', border: '1px solid #38bdf8', borderRadius: 4,
                          color: '#e2e8f0', fontSize: 11, padding: '2px 8px', cursor: 'pointer',
                        }}
                      >Save</button>
                    </div>
                  </div>
                ) : (
                  <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{msg.text}</div>
                )}
                {isSelf && !isEditing && (
                  <div style={{ display: 'flex', gap: 6, marginTop: 4, justifyContent: 'flex-end' }}>
                    <button
                      onClick={() => { setEditingId(msg.id); setEditText(msg.text); }}
                      style={{ background: 'transparent', border: '1px solid #334155', borderRadius: 4, color: '#94a3b8', fontSize: 11, cursor: 'pointer', padding: '1px 8px' }}
                    >edit</button>
                    <button
                      onClick={() => handleDelete(msg.id)}
                      style={{ background: 'transparent', border: '1px solid #7f1d1d', borderRadius: 4, color: '#f87171', fontSize: 11, cursor: 'pointer', padding: '1px 8px' }}
                    >delete</button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end' }}>
        <textarea
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Add a note... (Enter to send, Shift+Enter for newline)"
          rows={2}
          style={{
            flex: 1,
            background: '#060d18',
            border: '1px solid #1e3a5f',
            borderRadius: 6,
            color: '#e2e8f0',
            fontSize: 13,
            padding: '8px 10px',
            resize: 'vertical',
            outline: 'none',
            fontFamily: 'inherit',
            lineHeight: 1.5,
            boxSizing: 'border-box',
          }}
        />
        <button
          onClick={handleSend}
          disabled={!draft.trim()}
          style={{
            background: draft.trim() ? '#1e3a5f' : '#0a1120',
            border: `1px solid ${draft.trim() ? '#38bdf8' : '#1e293b'}`,
            borderRadius: 6,
            color: draft.trim() ? '#e2e8f0' : '#475569',
            fontSize: 13,
            fontWeight: 600,
            padding: '8px 16px',
            cursor: draft.trim() ? 'pointer' : 'default',
            whiteSpace: 'nowrap',
          }}
        >Send</button>
      </div>
    </div>
  );
}

export default CandidateNotes;
